import "../consensusActivity.css"
import { logError, LogCategory } from "utils/sentryLogger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCancel, faCopy, faEdit, faRobot, faSpinner, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Input, Label} from "@bootstrap-styled/v4";
import React, {useCallback, useEffect, useRef, useState} from "react";
import { useHistory, useLocation } from "react-router-dom";
import Select from "react-select";
import { Alert, Button, Modal } from "react-bootstrap";
import { request } from "hooks/useOkoApi/useOkoApi";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { useStandards } from "hooks/useStandards/useStandards";
import { buildSelectOptions} from "utils/standards";
import {
  buildPreviewChallenge,
  CHALLENGE_STATUS_OPTIONS,
  CHALLENGE_TYPE_FILTER_OPTIONS,
  getIllustrationOptions,
  matchesIllustration,
} from "utils/challenge";
import { EditConsensusQuestionModal } from "./EditConsensusQuestionModal";
import { ConsensusActivityRender } from "../ConsensusActivityRender";
import { CHALLENGE_QUALITY_LLM_PROMPT, colors, constants, generateRoutePath } from "utils";

// "<model>|<prompt_version>" round-trips as the run select's value.
const runToValue = (run) => (run ? `${run.model}|${run.prompt_version}` : null);

export const ConsensusQuestionRender = () => {
  const { authUserData, loading } = useAuthUserData();
  const history = useHistory();
  const location = useLocation();
  const standardsData = useStandards();
  const [questionsData, setQuestionsData] = useState([]);
  const [prefix, setPrefix] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [challengeToEdit, setChallengeToEdit] = useState();
  const [selectedChallengeType, setSelectedChallengeType] = useState("all");
  const [selectedChallengeStatus, setSelectedChallengeStatus] = useState("all");
  const [illustrationFilter, setIllustrationFilter] = useState("all");
  const [selectedSource, setSelectedSource] = useState(null);
  const [sourcesData, setSourcesData] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [availableRuns, setAvailableRuns] = useState([]);
  const runInitializedRef = useRef(false);
  const [inactiveList, setInactiveList] = useState([]);
  const [isShowInactiveModal, setIsShowInactiveModal] = useState(false);
  const [inactiveAPIResponse, setInactiveAPIResponse] = useState();
  const svgCross = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40">
    <path fill="red" d="M6 6 L18 18 M18 6 L6 18" stroke="red" stroke-width="2" stroke-linecap="round" opacity="0.75"/>
  </svg>`;
  const svgCrossDataURL = `data:image/svg+xml;utf8,${encodeURIComponent(svgCross)}`;
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [llmList, setLlmList] = useState([]);
  const [llmResponse, setLlmResponse] = useState();
  const [isShowLLMModal, setIsShowLLMModal] = useState(false);
  const [model, setModel] = useState("gpt-4.1");
  const [llmPrompt, setLlmPrompt] = useState(CHALLENGE_QUALITY_LLM_PROMPT);
  const [llmBatchProgress, setLlmBatchProgress] = useState();
  const [llmBatchSize, setLlmBatchSize] = useState(50);
  const [challengeIdList, setChallengeIdList] = useState([]);
  const [fetchMode, setFetchMode] = useState("filter"); // "filter" or "ids"
  const [idInputText, setIdInputText] = useState("");
  const [idFetchError, setIdFetchError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  useEffect(() => {
    if (needsRefresh && fetchMode === "filter") {
      getQuestionsData(prefix, selectedChallengeType, selectedSource);
      setNeedsRefresh(false);
    }
  }, [needsRefresh, fetchMode]);

  useEffect(() => {
    (async () => {
      try {
        const sources = await request("questions/sources", { method: "GET" });
        setSourcesData(sources || []);
      } catch (e) {
        logError("Failed to fetch challenge sources", e, {
          category: LogCategory.ACTIVITY,
          activityType: "consensus",
        });
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const resp = await request("questions/fetch/challenges?limit=1&offset=0", { method: "GET" });
        setAvailableRuns(resp.available_runs || []);
        if (!runInitializedRef.current) {
          runInitializedRef.current = true;
          if (resp.selected_run) setSelectedRun(runToValue(resp.selected_run));
        }
      } catch (e) {
        logError("Failed to fetch benchmark runs", e, {
          category: LogCategory.ACTIVITY,
          activityType: "consensus",
        });
      }
    })();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const idsParam = searchParams.get('ids');

    if (idsParam) {
      // Switch to ID mode and populate the input
      setFetchMode("ids");
      setIdInputText(idsParam);
      const challengeIds = parseIdInput(idsParam);
      if (challengeIds.length > 0) {
        getQuestionsByIds(challengeIds);
      }
    }
  }, [location.search]);

  const getQuestionsData = async (prefix, selectedFormat, source, run = selectedRun) => {
    try {
      setIsLoading(true);
      let fetch_inactive = true;
      if (selectedChallengeStatus === "active") {
        fetch_inactive = false;
      }
      const formatsToFetch = selectedFormat === "all"
        ? ["multiplechoice", "openresponse"]
        : [selectedFormat];
      const params = `fetch_inactive=${fetch_inactive}` +
        `${prefix ? `&standard_prefix=${prefix}` : ''}` +
        `${source ? `&source=${encodeURIComponent(source)}` : ''}` +
        `${run ? `&run=${encodeURIComponent(run)}` : ''}`;
      const results = await Promise.all(
        formatsToFetch.map((format) => request(`questions/fetch/${format}?${params}`, { method: "GET" }))
      );
      let questionsData = results.flat();
      if (selectedChallengeStatus === "inactive") {
        questionsData = questionsData.filter((i) => i.status === "inactive");
      }
      setQuestionsData(questionsData);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      if (!handle403Error(e)) {
        logError("Failed to fetch consensus questions", e, {
          category: LogCategory.ACTIVITY,
          activityType: "consensus",
        });
      }
    }
  };

  const getQuestionsByIds = async (challengeIds, run = selectedRun) => {
    try {
      setIsLoading(true);
      setIdFetchError(null);
      let questionsData = await request(
        `questions/fetch_by_ids${run ? `?run=${encodeURIComponent(run)}` : ''}`, {
          method: "POST",
          payload: { challenge_ids: challengeIds },
        });

      setQuestionsData(questionsData);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      if (!handle403Error(e)) {
        const errorMessage = e?.detail || e?.message || "Failed to fetch challenges by IDs. Please check the IDs and try again.";
        setIdFetchError(errorMessage);
        logError("Failed to fetch challenges by IDs", e, {
          category: LogCategory.ACTIVITY,
          activityType: "consensus",
        });
      }
    }
  };
  const handleOnStandardsChange = (target) => {
    if (prefix === target.value) {
      return;
    }
    setPrefix(target.value);
    setCurrentPage(1);
    if (fetchMode === "filter") {
      getQuestionsData(target.value, selectedChallengeType, selectedSource);
    }
  };
  const handleOnFormatChange = (target) => {
    if (selectedChallengeType === target.value) {
      return;
    }
    setSelectedChallengeType(target.value);
    setCurrentPage(1);
    if (fetchMode === "filter") {
      getQuestionsData(prefix, target.value, selectedSource);
    }
  };
  const handleOnSourceChange = (target) => {
    if (selectedSource === target.value) {
      return;
    }
    setSelectedSource(target.value);
    setCurrentPage(1);
    if (fetchMode === "filter") {
      getQuestionsData(prefix, selectedChallengeType, target.value);
    }
  };
  const handleOnStatusChange = (target) => {
    if (selectedChallengeStatus === target.value) {
      return;
    }
    setSelectedChallengeStatus(target.value);
    setCurrentPage(1);
    setNeedsRefresh(true);
  };
  const handleOnRunChange = (target) => {
    if (selectedRun === target.value) {
      return;
    }
    setSelectedRun(target.value);
    setCurrentPage(1);
    if (fetchMode === "filter") {
      getQuestionsData(prefix, selectedChallengeType, selectedSource, target.value);
    } else if (fetchMode === "ids") {
      const challengeIds = parseIdInput(idInputText);
      if (challengeIds.length > 0) {
        getQuestionsByIds(challengeIds, target.value);
      }
    }
  };

  const handleOnIllustrationChange = (target) => {
    setIllustrationFilter(target.value);
    setCurrentPage(1);
  };

  const handleFetchModeChange = (mode) => {
    setFetchMode(mode);
    setIdFetchError(null);
    setInactiveList([]);
    setLlmList([]);
    setChallengeIdList([]);
    setCurrentPage(1);

    if (mode === "filter") {
      // Remove ids parameter from URL when switching to filter mode
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete('ids');
      const newUrl = `${location.pathname}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
      history.replace(newUrl);
      setQuestionsData([]);
      setPrefix("");
    } else if (mode === "ids") {
      setQuestionsData([]);
    }
  };

  const handleIdInputChange = (e) => {
    setIdInputText(e.target.value);
    if (idFetchError) {
      setIdFetchError(null);
    }
  };

  const parseIdInput = (inputText) => {
    // Handle various input formats: quotes, double quotes, trailing commas, etc.
    return inputText
      .split(",")
      .map(id => {
        let cleanId = id.trim();
        while (cleanId.endsWith(",")) {
          cleanId = cleanId.slice(0, -1).trim();
        }
        if ((cleanId.startsWith('"') && cleanId.endsWith('"')) ||
            (cleanId.startsWith("'") && cleanId.endsWith("'"))) {
          cleanId = cleanId.slice(1, -1).trim();
        }
        // Handle cases where there might be extra quotes inside
        cleanId = cleanId.replace(/^["']+|["']+$/g, '');

        return cleanId;
      })
      .filter(id => id.length > 0);
  };

  const handleFetchByIds = () => {
    const challengeIds = parseIdInput(idInputText);

    if (challengeIds.length === 0) {
      setIdFetchError("Please enter at least one challenge ID");
      return;
    }

    // Update URL with the current IDs for shareability
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('ids', challengeIds.join(','));
    const newUrl = `${location.pathname}?${searchParams.toString()}`;
    history.replace(newUrl);

    getQuestionsByIds(challengeIds);
  };

  const getFilteredData = () => {
    return questionsData.filter(challenge => matchesIllustration(challenge, illustrationFilter));
  };

  const getIllustrationTypesFromData = () => {
    const types = new Set();
    questionsData.forEach((challenge) => {
      const type = challenge.illustration_taxonomy?.wren_illustration_type;
      if (type) types.add(type);
    });
    return [...types];
  };

  const getPaginatedData = () => {
    const filteredData = getFilteredData();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const filteredData = getFilteredData();
    return Math.ceil(filteredData.length / itemsPerPage);
  };

  const getPageInfo = () => {
    const filteredData = getFilteredData();
    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(currentPage * itemsPerPage, filteredData.length);
    return {
      startIndex: filteredData.length > 0 ? startIndex : 0,
      endIndex,
      totalItems: filteredData.length
    };
  };

  const handlePageChange = (newPage) => {
    const totalPages = getTotalPages();
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handle403Error = (error) => {
    if (error?.status === 403 ||
        error?.detail?.includes("403") ||
        error?.detail?.includes("Forbidden") ||
        error?.detail?.includes("Access denied") ||
        error?.message?.includes("403") ||
        error?.message?.includes("Forbidden")) {
      console.log("403 error detected, redirecting to admin login");
      history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));
      return true;
    }
    return false;
  };
  const getOptions = () => {
    const options = buildSelectOptions(standardsData);
    options.unshift({"value": null, label: "All available standards"});
    return options;
  };
  const getSourceOptions = () => {
    const options = sourcesData.map((s) => ({
      value: s.source,
      label: `${s.source}  (${s.mc_count} MC / ${s.or_count} OR)`,
    }));
    options.unshift({ value: null, label: "All item sets" });
    return options;
  };
  const getRunOptions = () =>
    availableRuns.map((r) => ({
      value: runToValue(r),
      label: `${r.model} · ${r.prompt_version} (${r.question_count})`,
    }));
  const onEdit = (challenge) => {
    setChallengeToEdit(challenge);
    setIsModalOpen(true);
  };
  const onAddToInactiveList = (challenge) => {
    setInactiveList(prevList => [...prevList, challenge])
  }
  const onRemoveFromInactiveList = (challenge) => {
    setInactiveList(prevList => prevList.filter(item => item.id !== challenge.id));
  };
  const onToggleInactiveModal = () => {
    setInactiveAPIResponse(null);
    setIsShowInactiveModal(prevState => !prevState);
  }
  const onAddToLLMList = (challenge) => {
    setLlmList(prevList => [...prevList, challenge])
  }
  const onRemoveFromLLMList = (challenge) => {
    setLlmList(prevList => prevList.filter(item => item.id !== challenge.id));
  };
  const onToggleLLMModal = () => {
    setLlmResponse(null);
    setLlmBatchProgress(null);
    setIsShowLLMModal(prevState => !prevState);
  }
  const onAddChallengeIdToList = (challenge) => {
    setChallengeIdList(prevList => [...prevList, challenge.id])
  }
  const onRemoveChallengeIdFromList = (challenge) => {
    setChallengeIdList(prevList => prevList.filter(item => item !== challenge.id));
  };
  const onCopyCsvChallengeIds = () => {
    const csvChallengeIds = challengeIdList.join(",");
    navigator.clipboard.writeText(csvChallengeIds).then(() => {});
  }

  const onMarkChallengesInactive = async () => {
    if (!inactiveList.length) return;
    try {
      setIsLoading(true);
      console.log(`Marking ${inactiveList.length} challenges as inactive...`);
      const response = await request(
        "questions/mark_inactive", {
          method: "POST",
          payload: inactiveList
        });
      if (typeof response === "string") {
        setInactiveAPIResponse(response);
      } else {
        setInactiveAPIResponse("Some errors have occurred: " + JSON.stringify(response));
      }
      setInactiveList([]);
      setLlmList([]);
      setChallengeIdList([]);
      getQuestionsData(prefix, selectedChallengeType, selectedSource);
      setIsLoading(false);
    } catch (e) {
      console.error(e);
      setIsLoading(false);
      if (!handle403Error(e)) {
        // Error already logged above
      }
    }
  }

  const onShipToLLM = async () => {
    if (!llmList.length) return;
    setIsLoading(true);
    setLlmResponse(null);

    const challengeIds = llmList.map(item => item.id);
    const challengeIdBatches = chunkArray(challengeIds, llmBatchSize);
    let allResponses = [];
    let csvContent = "id,flagged_reason,source,external_id,prompt,answer_options,solution_index,solution,prompt_image_url,internal_image_url\n";

    try {
      for (let i = 0; i < challengeIdBatches.length; i++) {
        try {
          const processedCount = Math.min((i + 1) * llmBatchSize, challengeIds.length);
          const batchProgress = `Processing ${processedCount} challenges out of ${challengeIds.length}...`;
          console.log(batchProgress);
          setLlmBatchProgress(batchProgress);
          console.dir(challengeIdBatches[i]);
          const response = await request("questions/llm_report", {
            payload: {
              challenge_ids: challengeIdBatches[i],
              provider: "OpenAI",
              model: model,
              system_prompt: llmPrompt,
              temperature: 0,
            }
          });
          if (response) {
            console.log(response);
            const parsedResponse = JSON.parse(stripJsonWrapper(response));
            parsedResponse.forEach(item => {
              const id = `"${item.id}"`;
              const reason = `"${item.flagged_reason.replace(/"/g, '""')}"`;
              const matchingChallenge = llmList.find(challenge => challenge.id === item.id);
              const source = `"${matchingChallenge.source}"`;
              const external_id = matchingChallenge.external_id
                ? `"${matchingChallenge.external_id}"`
                : "";
              const prompt = `"${matchingChallenge.prompt.replace(/"/g, '""')}"`;
              const answer_options = matchingChallenge.answer_options
                ? `"${JSON.stringify(matchingChallenge.answer_options).replace(/"/g, '""')}"`
                : "";
              const solution_index = matchingChallenge.solution_index === null
              || matchingChallenge.solution_index === undefined
                ? ""
                : `"${matchingChallenge.solution_index}"`;
              const solution = matchingChallenge.solution === null
              || matchingChallenge.solution === undefined
                ? ""
                : `"${matchingChallenge.solution}"`;
              const prompt_image_url = matchingChallenge.prompt_image_url
                ? `"${matchingChallenge.prompt_image_url}"`
                : "";
              const internal_image_url = matchingChallenge.internal_image_url
                ? `"${matchingChallenge.internal_image_url}"`
                : "";
              const row = `${id},${reason},${source},${external_id},${prompt},${answer_options},${solution_index},${solution},${prompt_image_url},${internal_image_url}\n`;
              csvContent += row;
            });
            allResponses = allResponses.concat(parsedResponse);
            setLlmResponse(allResponses);
          }
          if (i < challengeIdBatches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } catch (e) {
          if (!handle403Error(e)) {
            setLlmResponse(`Batch ${i + 1} has failed, but the report will continue with the remaining ones...`);
            console.error(e);
          }
        }
      }
      if (allResponses.length) {
        // Create a Blob from the CSV string and trigger the download
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "llm_responses.csv";
        link.click();
      } else {
        setLlmResponse("No challenges were flagged by LLM.");
      }
    } catch (e) {
      if (!handle403Error(e)) {
        setLlmResponse("Error has occurred (see console for details).");
        console.error(e);
      }
    } finally {
      setIsLoading(false);
      if (allResponses.length) {
        setLlmBatchProgress("CSV report should automatically download.");
      } else {
        setLlmBatchProgress("Done!");
      }
      setInactiveList([]);
    }
  };

  const styles = {
    control: (css) => ({
      ...css,
      width: "calc(100vw - 312px - 44px)",
      maxWidth: "800px"
    }),
  };
  const placeholderPrefix = prefix ? `for standard ${prefix}` : "";

  const handleKeyPress = useCallback((event) => {
    if (event.ctrlKey && event.shiftKey && event.key === "D") {
      setInactiveList([...questionsData]);
    }
    if (event.ctrlKey && event.shiftKey && event.key === "A") {
      setLlmList([...questionsData]);
    }
    if (event.ctrlKey && event.shiftKey && event.key === "Z") {
      setInactiveList([]);
      setLlmList([]);
      setChallengeIdList([]);
    }
  }, [questionsData]);

  const markForButtonStyle = {
    borderRadius: "100px",
    borderWidth: "1px",
    alignSelf: "center",
    width: "120px"
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [handleKeyPress]);

  useEffect(() => {
    setCurrentPage(1);
  }, [questionsData]);

  const PaginationControls = ({ position = "top" }) => {
    const pageInfo = getPageInfo();
    const totalPages = getTotalPages();

    if (pageInfo.totalItems === 0) return null;

    return (
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: position === "top" ? "16px" : "0",
        marginTop: position === "bottom" ? "16px" : "0",
        padding: "8px",
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
        flexWrap: "wrap",
        gap: "16px"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "14px", color: "#666" }}>
            Showing {pageInfo.startIndex}-{pageInfo.endIndex} of {pageInfo.totalItems} challenges
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "14px" }}>Items per page:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              style={{
                padding: "4px 8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                fontSize: "14px"
              }}
            >
              <option value={10}>10</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              backgroundColor: currentPage === 1 ? "#f5f5f5" : "white",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              fontSize: "14px"
            }}
          >
            Previous
          </button>

          <span style={{ fontSize: "14px", margin: "0 8px" }}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              backgroundColor: currentPage === totalPages ? "#f5f5f5" : "white",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              fontSize: "14px"
            }}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  if (loading) return <></>;
  if (authUserData.isAnonymous) history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));

  return (
    <div>
      {isLoading && <div style={{ left: "50%", position: "absolute", top: "50%"}}>
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          size="5x"
        />
      </div>}
      {inactiveList.length ? (<div style={{
        alignSelf: "flex-end",
        bottom: "15px",
        color: "red",
        right: "18px",
        cursor: "pointer",
        position: "fixed"}} onClick={onToggleInactiveModal}>
        <FontAwesomeIcon
          icon={faTrash}
          size="3x"
        />
      </div>) : null}
      {llmList.length ? (<div style={{
        alignSelf: "flex-end",
        bottom: "82px",
        color: "blue",
        right: "10px",
        cursor: "pointer",
        position: "fixed"}} onClick={onToggleLLMModal}>
        <FontAwesomeIcon
          icon={faRobot}
          size="3x"
        />
      </div>) : null}
      {challengeIdList.length ? (<div style={{
        alignSelf: "flex-end",
        bottom: "149px",
        color: "black",
        right: "18px",
        cursor: "pointer",
        position: "fixed"}} onClick={onCopyCsvChallengeIds}>
        <FontAwesomeIcon
          icon={faCopy}
          size="3x"
        />
      </div>) : null}
      <div style={{
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        justifySelf: "center",
        marginBottom: "35px",
        width: "calc(100vw - 312px - 44px)",
        maxWidth: "800px" }}>
        <p>
          {fetchMode === "ids"
            ? `Fetched ${questionsData.length} challenge(s) by IDs`
            : `Fetched ${questionsData.length} challenge(s) ${placeholderPrefix}`
          }
          {illustrationFilter !== "all" ?
            ` (showing ${getFilteredData().length} matching illustration filter)` : ''}
        </p>
        <br/>
        <div style={{ display: "flex", gap: "8px", marginBottom: "24px" }}>
          <button
            onClick={() => handleFetchModeChange("filter")}
            style={{
              backgroundColor: fetchMode === "filter" ? colors.nightlyWoods500 : "white",
              color: fetchMode === "filter" ? "white" : colors.nightlyWoods500,
              border: `1px solid ${colors.nightlyWoods500}`,
              borderRadius: "20px",
              padding: "8px 16px",
              cursor: "pointer",
              fontSize: "14px"
            }}
          >
            Filter Mode
          </button>
          <button
            onClick={() => handleFetchModeChange("ids")}
            style={{
              backgroundColor: fetchMode === "ids" ? colors.nightlyWoods500 : "white",
              color: fetchMode === "ids" ? "white" : colors.nightlyWoods500,
              border: `1px solid ${colors.nightlyWoods500}`,
              borderRadius: "20px",
              padding: "8px 16px",
              cursor: "pointer",
              fontSize: "14px"
            }}
          >
            Fetch by IDs
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px" }}>
          Benchmark run:
          <Select
            isDisabled={isLoading}
            value={getRunOptions().find(option => option.value === selectedRun) || null}
            isSearchable
            placeholder="Select benchmark run..."
            options={getRunOptions()}
            onChange={handleOnRunChange}
          />
        </div>

        {fetchMode === "ids" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
            {idFetchError && (
              <Alert variant="danger" style={{ marginBottom: "16px" }}>
                <strong>Error:</strong> {idFetchError}
              </Alert>
            )}
            <div style={{ display: "flex", gap: "16px", alignItems: "flex-end", width: "100%" }}>
              <div style={{ flex: 1 }}>
                <Label>Challenge IDs (comma-separated)</Label>
                <Input
                  type="textarea"
                  placeholder={`Enter challenge IDs separated by commas. Supports various formats:
id1, id2, id3
"id1", "id2", "id3"
'id1', 'id2', 'id3'`}
                  value={idInputText}
                  onChange={handleIdInputChange}
                  disabled={isLoading}
                  style={{ height: "80px", resize: "vertical" }}
                />
                {idInputText.trim() && (
                  <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                    Will fetch {parseIdInput(idInputText).length} challenge(s): {parseIdInput(idInputText).slice(0, 5).join(", ")}
                    {parseIdInput(idInputText).length > 5 && "..."}
                  </div>
                )}
              </div>
              <button
                onClick={handleFetchByIds}
                disabled={isLoading || !idInputText.trim()}
                style={{
                  alignSelf: "center",
                  backgroundColor: colors.nightlyWoods500,
                  color: "white",
                  border: "none",
                  borderRadius: "20px",
                  padding: "8px 16px",
                  cursor: isLoading || !idInputText.trim() ? "not-allowed" : "pointer",
                  opacity: isLoading || !idInputText.trim() ? 0.5 : 1,
                  fontSize: "14px",
                  height: "fit-content"
                }}
              >
                Fetch Challenges
              </button>
            </div>
          </div>
        ) : (
          <>
            <Select
          isDisabled={isLoading}
          isSearchable placeholder="Select standard..."
          options={getOptions()}
          onChange={handleOnStandardsChange}
          styles={styles}
        />
        <br/>
        <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            Challenge type:
            <Select
              isDisabled={isLoading}
              value={CHALLENGE_TYPE_FILTER_OPTIONS.find(option => option.value === selectedChallengeType)}
              isSearchable
              placeholder="Select challenge type..."
              options={CHALLENGE_TYPE_FILTER_OPTIONS}
              onChange={handleOnFormatChange}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            Item set:
            <Select
              isDisabled={isLoading}
              isSearchable
              value={getSourceOptions().find(option => option.value === selectedSource) || null}
              placeholder="Filter by item set / source..."
              options={getSourceOptions()}
              onChange={handleOnSourceChange}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            Status:
            <Select
              isDisabled={isLoading}
              value={CHALLENGE_STATUS_OPTIONS.find(option => option.value === selectedChallengeStatus)}
              isSearchable
              placeholder="Filter by status..."
              options={CHALLENGE_STATUS_OPTIONS}
              onChange={handleOnStatusChange}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            Illustration:
            <Select
              isDisabled={isLoading}
              value={getIllustrationOptions(getIllustrationTypesFromData()).find(option => option.value === illustrationFilter)}
              isSearchable
              placeholder="Filter by illustration..."
              options={getIllustrationOptions(getIllustrationTypesFromData())}
              onChange={handleOnIllustrationChange}
            />
          </div>
        </div>
          </>
        )}
      </div>

      {!isLoading && getFilteredData().length > 0 && <PaginationControls position="top" />}

      {!isLoading && <div style={{ alignItems: "center", display: "grid", justifyItems: "center" }}>
        {getPaginatedData().map((challenge) => (
          <div key={challenge.id} style={{ borderBottom: "1px solid black", display: "flex", marginBottom: "15px", gap: "24px" }}>
            <div style={{ position: "relative" }}>
              <ConsensusActivityRender
                hideQuestion={false}
                challenge={buildPreviewChallenge(challenge)}
                onMCAnswer={() => {}}
                onORAnswer={() => {}}
                isMathJaxSet={false} // Needed to prevent race condition on MathJax typesetting.
                showPromptFormula
              />
              {inactiveList.some(item => item.id === challenge.id) && (
                <img
                  src={svgCrossDataURL}
                  alt="Red Cross"
                  style={{
                    position: "absolute",
                    top: "0",
                    left: "0",
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    pointerEvents: "none",
                  }}
                />
              )}
            </div>
            <div style={{ alignSelf: "center", display: "flex", flexDirection: "column", fontSize: "10px", gap: "8px", textAlign: "center" }}>
              <span>ID: {challenge.id}</span>
              <span>Source: {challenge.source}</span>
              {challenge.external_id && <span>External ID: {challenge.external_id}</span>}
              <span>Standards: {challenge.standards?.join(", ") || "N/A"}</span>
              <span>Status: {challenge.status.toUpperCase()}</span>
              <button style={{
                borderRadius: "100px",
                borderWidth: "1px",
                alignSelf: "center"
              }} onClick={() => onEdit(challenge)}>
                <FontAwesomeIcon icon={faEdit} />
                &nbsp;Edit
              </button>
              {challenge.id && <button style={markForButtonStyle} onClick={() => {
                if (inactiveList.some(item => item.id === challenge.id)) {
                  onRemoveFromInactiveList(challenge);
                } else {
                  onAddToInactiveList(challenge);
                }
              }}>
                {inactiveList.some((item) => item.id === challenge.id)
                  ? (<><FontAwesomeIcon icon={faCancel} /> &nbsp;Undo Mark Inactive</>)
                  : (<><FontAwesomeIcon icon={faTrash} /> &nbsp;Mark Inactive</>)
                }
              </button>}
              {challenge.id && <button style={markForButtonStyle} onClick={() => {
                if (llmList.some(item => item.id === challenge.id)) {
                  onRemoveFromLLMList(challenge);
                } else {
                  onAddToLLMList(challenge);
                }
              }}>
                {llmList.some((item) => item.id === challenge.id)
                  ? (<><FontAwesomeIcon icon={faCancel} /> &nbsp;Unmark For LLM</>)
                  : (<><FontAwesomeIcon icon={faRobot} /> &nbsp;Mark For LLM</>)
                }
              </button>}
              {challenge.id && <button style={markForButtonStyle} onClick={() => {
                if (challengeIdList.some(item => item === challenge.id)) {
                  onRemoveChallengeIdFromList(challenge);
                } else {
                  onAddChallengeIdToList(challenge);
                }
              }}>
                {challengeIdList.some((item) => item === challenge.id)
                  ? (<><FontAwesomeIcon icon={faCancel} /> &nbsp;Unmark For ID Copy</>)
                  : (<><FontAwesomeIcon icon={faCopy} /> &nbsp;Mark For ID Copy</>)
                }
              </button>}
            </div>
          </div>
        ))}
      </div>
      }

      {!isLoading && getFilteredData().length > 0 && <PaginationControls position="bottom" />}
      {challengeToEdit && <EditConsensusQuestionModal challenge={challengeToEdit}
                                                      isModalOpen={isModalOpen}
                                                      modalTitle="Edit Challenge"
                                                      setIsModalOpen={setIsModalOpen}
                                                      setNeedsRefresh={setNeedsRefresh} />
      }
      <Modal show={isShowInactiveModal} size="lg" onHide={onToggleInactiveModal}>
        {isLoading && <div style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999
        }}>
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            size="5x"
          />
        </div>}
        <Modal.Header><span style={{ fontSize: "20px" }}>{!inactiveAPIResponse
          ? `Are you sure?`
          : `Mark Inactive Results`}</span></Modal.Header>
        <Modal.Body>
          {inactiveAPIResponse
            ? <pre>{inactiveAPIResponse}</pre>
            : <span>
              You are about to mark {inactiveList.length} challenge(s) inactive.
          </span>}
        </Modal.Body>
        <Modal.Footer>
          {!inactiveAPIResponse && <Button
            onClick={onMarkChallengesInactive}
            style={{
              backgroundColor: colors.pink500,
              border: "none",
              borderRadius: "20px",
              fontSize: "16px",
              cursor: isLoading || !inactiveList.length ? "not-allowed" : "pointer",
              opacity: isLoading || !inactiveList.length ? 0.5 : 1
            }}
          >
            Mark Inactive
          </Button>}
          <Button onClick={onToggleInactiveModal}
                  disabled={isLoading}
                  style={{
                    backgroundColor: "white",
                    border: `1px solid ${colors.nightlyWoods500}`,
                    borderRadius: "20px",
                    cursor: isLoading ? "not-allowed" : "pointer",
                    color: colors.nightlyWoods500,
                    opacity: isLoading ? 0.5 : 1,
                    fontSize: "16px" }}
          >
            {inactiveAPIResponse ? <>Close</> : <>Cancel</>}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={isShowLLMModal} onHide={onToggleLLMModal} size="lg">
        {isLoading && <div style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999
        }}>
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            size="5x"
          />
        </div>}
        <Modal.Header><span style={{ fontSize: "20px" }}>LLM Challenge Quality Control Tool</span></Modal.Header>
        <Modal.Body>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div>
              <Label>Model (OpenAI only as of now)</Label>
              <Input placeholder="LLM"
                     type="text"
                     onChange={(e) => setModel(e.target.value)}
                     value={model} />
            </div>
            <div>
              <Label>LLM Prompt</Label>
              <Input placeholder="LLM Prompt"
                     type="textarea"
                     style={{ fontSize: "12px", height: "350px" }}
                     onChange={(e) => setLlmPrompt(e.target.value)}
                     value={llmPrompt} />
            </div>
            <div>
              <Label>Batch size</Label>
              <Input placeholder="Number of challenges in a batch, 50 is recommended"
                     type="number"
                     min={1}
                     max={100}
                     onChange={(e) => setLlmBatchSize(e.target.value)}
                     value={llmBatchSize} />
            </div>
            {llmBatchProgress ?? null}
            <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
            {llmResponse && Array.isArray(llmResponse) && llmResponse.length
              ? JSON.stringify(llmResponse, null, 2)
              : llmResponse}
          </pre>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={onShipToLLM}
            style={{
              backgroundColor: colors.pink500,
              border: "none",
              borderRadius: "20px",
              fontSize: "16px",
              cursor: isLoading || !llmList.length ? "not-allowed" : "pointer",
              opacity: isLoading || !llmList.length ? 0.5 : 1
            }}
          >
            Send to LLM
          </Button>
          <Button onClick={onToggleLLMModal}
                  style={{
                    backgroundColor: "white",
                    border: `1px solid ${colors.nightlyWoods500}`,
                    borderRadius: "20px",
                    color: colors.nightlyWoods500,
                    fontSize: "16px" }}
          >
            {llmResponse ? <>Close</> : <>Cancel</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const chunkArray = (arr, size) => {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size)
  );
};

const stripJsonWrapper = (response) => {
  const match = response.match(/^```json\s*([\s\S]*?)\s*```$/);
  return match ? match[1] : response;
}
